import { useCallback, useEffect, useRef } from 'react';

interface VimeoEmbedProps {
  /** A player.vimeo.com embed URL. Run admin-supplied input through normalizeVimeoUrl first. */
  src: string;
  /** Accessible title for the iframe. */
  title: string;
  /** Fires once the viewer reaches the end of the video. */
  onFinish?: () => void;
  className?: string;
}

/**
 * Vimeo player with end-of-video detection.
 *
 * Uses Vimeo's postMessage API rather than the @vimeo/player SDK, which is not a
 * dependency of this project. Appending `api=1` to the src makes the player emit
 * events; we subscribe to `finish` on iframe load and listen for it on window.
 *
 * This is a binary signal (finished or not). There is no percent-watched or
 * resume-position tracking.
 */
export default function VimeoEmbed({ src, title, onFinish, className }: VimeoEmbedProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Build embed src with api=1 so postMessage events fire
  const embedSrc = src + (src.includes('?') ? '&' : '?') + 'api=1';

  const handleFinish = useCallback(() => {
    onFinish?.();
  }, [onFinish]);

  useEffect(() => {
    if (!onFinish) return;
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== 'https://player.vimeo.com') return;
      // Only react to this player's iframe, not any other Vimeo embed on the page.
      if (iframeRef.current && e.source !== iframeRef.current.contentWindow) return;
      try {
        const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        if (data?.event === 'finish') handleFinish();
      } catch { /* ignore malformed messages */ }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [handleFinish, onFinish]);

  // Tell the Vimeo player to send events once it's ready
  const handleIframeLoad = () => {
    if (!onFinish) return;
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({ method: 'addEventListener', value: 'finish' }),
      'https://player.vimeo.com',
    );
  };

  return (
    <div className={className ?? 'aspect-video w-full bg-black'}>
      <iframe
        ref={iframeRef}
        src={embedSrc}
        onLoad={handleIframeLoad}
        className="w-full h-full"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        title={title}
      />
    </div>
  );
}
